import { useEffect } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useSession } from '../layout/TopBar';
import { routeBase, shortDate, studyName, studyThumbnail, useCollectionMutations, useOpenStudy } from '../api/collections';
import { toThread, useCommentMutations, useThread } from '../api/discussion';
import { publicThumbnail, usePublicCollection, type PublicCollection } from '../api/explore';
import { AuthorChip } from '../ui/Avatar';
import { Button } from '../ui/Button';
import { StudyCard } from '../ui/Cards';
import { CommentThread } from '../ui/CommentThread';
import { confirm } from '../ui/Confirm';
import { IconComment, IconHeart } from '../ui/icons';
import { RichText } from '../ui/RichText';
import { toast } from '../ui/toast';

/**
 * A Public collection, read only (`is.pubcol`): title, description, author
 * and stats, Save and Comment, the sections (groups first, then Studies), and
 * the Discussion. A guest browses everything; opening a study needs an account
 * (decided 2026-09-15). A Shared collection is not reachable here by id: its
 * link renders the same view through the link (community/share/ShareRoute.tsx).
 */
export function PublicCollectionPage() {
  const { publicId } = useParams<{ publicId: string }>();
  const { data: me, isLoading } = useSession();
  const guest = isLoading ? undefined : !me?.userId;
  const { data: c, isSuccess } = usePublicCollection(guest, publicId);
  const openStudy = useOpenStudy();

  if (!c) {
    // Answered, and nothing this person may see: a private or shared
    // collection, or one that is gone. A guest goes to the one sign-in screen
    // and comes back here after (AuthPage reads `return`).
    if (isSuccess && c === null) {
      if (guest) return <Navigate replace to={`${routeBase()}/signin?return=${encodeURIComponent(window.location.pathname + window.location.search)}`} />;
      return (
        <div style={{ flex: 1, display: 'grid', placeItems: 'center', padding: 40, textAlign: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>This collection is not available</span>
            <span style={{ fontSize: 13.5, color: '#6b6b70' }}>It is private, or it no longer exists.</span>
            <Link to={`${routeBase()}/explore`} style={{ fontSize: 13.5, marginTop: 4 }}>Go to Explore</Link>
          </div>
        </div>
      );
    }
    return <div style={{ flex: 1 }} />;
  }
  return (
    <CollectionView
      c={c}
      guest={guest !== false}
      thumb={(id) => (guest ? publicThumbnail(id) : studyThumbnail(id))}
      onOpenStudy={(id) => openStudy(id)}
    />
  );
}

/**
 * The read-only collection page itself. `shareId` renders it for a share
 * link's holder: the discussion goes through the link, and there is nothing to
 * save (liking is not part of what a link grants).
 */
export function CollectionView({
  c,
  guest,
  thumb,
  onOpenStudy,
  shareId,
}: {
  c: PublicCollection;
  guest: boolean;
  /** Both by the study's public id. */
  thumb: (studyId: string) => string;
  onOpenStudy: (studyId: string) => void;
  shareId?: string;
}) {
  const nav = useNavigate();
  const { data: me } = useSession();
  const m = useCollectionMutations();
  const thread = useThread('collection', c.public_id, guest, shareId);
  const cm = useCommentMutations('collection', c.public_id, shareId);
  const signup = (what: string) => toast(`Create a free account to ${what}`, { label: 'Create account', fn: () => nav('/signup') });
  const sections = [
    ...c.groups.map((g, i) => ({ key: g.public_id, num: String(i + 1).padStart(2, '0'), name: g.name, desc: g.description, isGroup: true, studies: c.studies.filter((s) => s.group_public_id === g.public_id) })),
    { key: 'root', num: '', name: 'Studies', desc: null as string | null, isGroup: false, studies: c.studies.filter((s) => !s.group_public_id) },
  ].filter((x) => x.studies.length);
  const stats = `${c.studies.length} ${c.studies.length === 1 ? 'study' : 'studies'} · ${c.groups.length} ${c.groups.length === 1 ? 'group' : 'groups'}`;
  // A group's link (#grp_…, the viewer's breadcrumb) scrolls to its band, as the member page does.
  const hash = typeof window === 'undefined' ? '' : window.location.hash.slice(1);
  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(`[data-grp="${CSS.escape(hash)}"]`);
    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
  }, [hash, c.public_id]);
  const goDiscussion = () => {
    const el = document.querySelector('[data-disc]');
    if (!el) return;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 100, behavior: 'smooth' });
  };

  return (
    <div className="tr-page" style={{ padding: '28px 28px 44px', display: 'flex', flexDirection: 'column', gap: 28, maxWidth: 1440, width: '100%', margin: '0 auto', flex: 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#6b6b70' }}>
        <Link to={`${routeBase()}/explore`} style={{ color: '#6b6b70', textDecoration: 'none' }}>Explore</Link>
        <span>/</span>
        <span className="tr-ellipsis" style={{ color: '#0f0f10' }}>{c.name}</span>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24, marginTop: -16 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 28, fontWeight: 600, letterSpacing: '-.02em', lineHeight: 1.2 }}>{c.name}</h1>
          <div style={{ width: '100%' }}><RichText html={c.description ?? ''} editable={false} /></div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 13, color: '#6b6b70', flexWrap: 'wrap' }}>
            {c.author ? <AuthorChip author={c.author} size={22} fontSize={13} color="#0f0f10" weight={500} /> : null}
            <span style={{ whiteSpace: 'nowrap' }}>{stats}</span>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
          {shareId ? null : (
            <Button variant="outlined" size={32} title="Save to your collections" icon={<IconHeart size={14} fill={c.liked ? '#d9342b' : 'none'} color={c.liked ? '#d9342b' : 'currentColor'} />} onClick={() => (guest ? signup('save collections') : m.like.mutate({ kind: 'collection', id: c.public_id, liked: c.liked }))}>
              {c.like_count}
            </Button>
          )}
          <Button variant="outlined" size={32} icon={<IconComment size={14} />} onClick={goDiscussion}>Comment</Button>
        </div>
      </div>
      {sections.map((sec) => (
        <div key={sec.key} data-grp={sec.key} style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: sec.isGroup ? '18px 20px 22px' : 0, border: `1px solid ${sec.isGroup ? '#ebebea' : 'transparent'}`, borderRadius: 12, background: sec.isGroup ? '#fafaf9' : 'transparent' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              {sec.isGroup ? <span style={{ fontSize: 12, fontWeight: 600, color: '#8a8a8f', fontFamily: "'JetBrains Mono', monospace", flex: 'none', minWidth: 18 }}>{sec.num}</span> : null}
              <h2 className="tr-ellipsis" style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{sec.name}</h2>
            </div>
            {sec.desc ? <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.5, color: '#3f3f44', maxWidth: 720 }}>{sec.desc}</p> : null}
            <span style={{ fontSize: 12.5, color: '#8a8a8f' }}>{sec.studies.length} {sec.studies.length === 1 ? 'study' : 'studies'}</span>
          </div>
          <div className="tr-cols-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '24px 20px' }}>
            {sec.studies.map((s) => (
              <StudyCard
                key={s.public_id}
                cover={thumb(s.public_id)}
                count={s.images}
                vis="none"
                comments={s.comment_count}
                name={studyName(s)}
                desc={s.description ?? ''}
                diagnosis={s.diagnosis ?? ''}
                meta={shortDate(s.created_utc)}
                likes={s.like_count}
                liked={s.liked}
                onOpen={() => onOpenStudy(s.public_id)}
                onToggleLike={() => (shareId ? undefined : guest ? signup('save studies') : m.like.mutate({ kind: 'study', id: s.public_id, liked: s.liked }))}
              />
            ))}
          </div>
        </div>
      ))}
      <div data-disc="1" style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 28, borderTop: '1px solid #ebebea', maxWidth: 760 }}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Discussion</h2>
          <span style={{ fontSize: 13, color: '#8a8a8f' }}>{c.comment_count}</span>
        </div>
        <CommentThread
          comments={toThread(thread.data?.comments ?? [])}
          me={{ name: me?.displayName || me?.email || '' }}
          canPost={!guest}
          placeholder="Comment on this collection"
          emptyText="No comments yet."
          onSignin={() => nav(`${routeBase()}/signin?return=${encodeURIComponent(window.location.pathname + window.location.search)}`)}
          onPost={(body) => cm.post.mutate({ body })}
          onReply={(parentId, body) => cm.post.mutate({ body, parentId })}
          onEdit={(commentId, body) => cm.edit.mutate({ commentId, body })}
          onDelete={async (commentId) => { if (await confirm({ title: 'Delete this comment?', action: 'Delete', danger: true })) cm.remove.mutate(commentId); }}
        />
      </div>
    </div>
  );
}
